import { useMutation, useQuery } from "@tanstack/react-query";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "../apiClient";

export const useGetAccountTree = () =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-account-tree"],
    queryFn: async () => {
      return apiClient.get(`/api/chart-of-accounts/tree`);
    },
  });

export const useGetTreeAccountsList = (params?: {
  category_system_key?: "expenses" | "sales" | "supplier_advance" | "vendor_advances"; //etc;
  /** Filter accounts by one or more category system keys (e.g. cash_and_cash_equivalent, ar_aggregators). */
  category_system_keys?: string[];
}) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-tree-accounts-list",
      params?.category_system_key ?? "",
      (params?.category_system_keys ?? []).join(","),
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.category_system_key) {
        searchParams.set("category_system_key", params.category_system_key);
      }
      (params?.category_system_keys ?? []).forEach((key) =>
        searchParams.append("category_system_keys[]", key),
      );
      const query = searchParams.toString();
      return apiClient.get(
        `/api/chart-of-accounts/accounts${query ? `?${query}` : ""}`,
      );
    },
  });

export const useGetTreeFixedAssetAccountsList = () =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-fixed-asset-tree-accounts"],
    queryFn: async () => {
      return apiClient.get(
        `/api/chart-of-accounts/accounts/fixed-asset-sale-accounts`,
      );
    },
  });

// records
interface IRecordBody {
  id?: number | string;
  chart_account_id?: number;
  source_type?: string;
  source_id?: string;
  name: string;
  balance: number;
  show_at_client_invoices?: boolean;
}

export const useAddRecordMutation = () =>
  useMutation<void, APIErrorResponse, IRecordBody>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/chart-of-accounts/records`, data);
    },
  });

export const useUpdateRecordMutation = () =>
  useMutation<void, APIErrorResponse, IRecordBody>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/chart-of-accounts/records/${id}`, rest);
    },
  });

export const useDeleteRecordMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/chart-of-accounts/records/${id}`);
    },
  });

interface IAccountBody {
  id?: number | string;
  chart_account_category_id?: number;
  name: string;
  start_balance?: number;
  show_at_client_invoices?: boolean;
}

export const useAddAccountMutation = () =>
  useMutation<void, APIErrorResponse, IAccountBody>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/chart-of-accounts/accounts`, data);
    },
  });

export const useUpdateAccountMutation = () =>
  useMutation<void, APIErrorResponse, IAccountBody>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/chart-of-accounts/accounts/${id}`, rest);
    },
  });

export const useDeleteAccountMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/chart-of-accounts/accounts/${id}`);
    },
  });

export const useUpdateTreeCategoryMutation = () =>
  useMutation<void, APIErrorResponse, IAccountBody>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/chart-of-accounts/categories/${id}`, rest);
    },
  });
